import { TableRow, TableCell, Typography } from '@mui/material';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import MultipleProgressBar from '../../../CustomComponents/MultipleProgressBar';
import { theme } from '../../../../theme';
import { Box } from '@mui/system';
import ProgressTooltip from './ProgressTooltip';

function PhysicianRow(props) {
    const recommendationSummary = props.physician.recommendation_summary;

    const opportunityValue = recommendationSummary.original_opportunity / 100;
    const totalValue = recommendationSummary.total_value / 100;
    const acceptedValue = recommendationSummary.accepted_value / 100;
    const customValue = recommendationSummary.custom_value / 100;
    const approvedValue = (recommendationSummary.accepted_value + recommendationSummary.custom_value) / 100;
    const approvedPercent = (Math.ceil(recommendationSummary.accepted_value + recommendationSummary.custom_value) / Math.max(recommendationSummary.original_opportunity, 1)) * 100;
    const approvedCount = recommendationSummary.num_accepted + recommendationSummary.num_custom;
    const approvedCountPercent = Math.round((approvedCount / Math.max(recommendationSummary.original_recs, 1)) * 100);
    const deniedValue = recommendationSummary.rejected_value / 100;
    const deniedPercent = (Math.ceil(deniedValue) / Math.max(opportunityValue, 1)) * 100;
    const omittedValue = recommendationSummary.omitted_value / 100;
    const omittedPercent = (Math.ceil(omittedValue) / Math.max(opportunityValue, 1)) * 100;
    const unreviewedValue = recommendationSummary.open_value / 100;
    const unreviewedPercent = (Math.ceil(unreviewedValue) / Math.max(opportunityValue, 1)) * 100;
    const unreviewedCountPercent = Math.round((recommendationSummary.num_open / Math.max(recommendationSummary.original_recs, 1)) * 100);
    const deniedCountPercent = Math.round((recommendationSummary.num_rejected / Math.max(recommendationSummary.original_recs, 1)) * 100);
    const omittedCountPercent = Math.round((recommendationSummary.num_omitted / Math.max(recommendationSummary.original_recs, 1)) * 100);

    // total reviewed values, rounded to nearest thousand
    const reviewedValueK = Math.round((totalValue - unreviewedValue) / 1000);
    const totalValueK = Math.round(totalValue / 1000);
    const reviewedValuePercent = Math.round((Math.ceil(recommendationSummary.accepted_value + recommendationSummary.custom_value + recommendationSummary.rejected_value) / Math.max(recommendationSummary.total_value, 1)) * 100);
    const reviewedCount = recommendationSummary.total_recs - recommendationSummary.num_open;
    const reviewedCountPercent = Math.round((reviewedCount / Math.max(recommendationSummary.total_recs, 1)) * 100);

    const progressValues = [
        {
            value: (Math.ceil(acceptedValue) / Math.max(totalValue, 1)) * 100,
            color: `${theme.palette.blue.main}`
        },
        {
            value: (Math.ceil(customValue) / Math.max(totalValue, 1)) * 100,
            color: `${theme.palette.background.customProgress}`
        },
        {
            value: (Math.ceil(deniedValue) / Math.max(totalValue, 1)) * 100,
            color: `${theme.palette.background.deniedProgress}`
        },
        {
            value: (Math.ceil(unreviewedValue) / Math.max(totalValue, 1)) * 100,
            color: `${theme.palette.background.unreviewedProgress}`
        }
    ];

    const progressCounts = [
        {
            value: (recommendationSummary.num_accepted / Math.max(recommendationSummary.total_recs, 1)) * 100,
            color: `${theme.palette.blue.main}`
        },
        {
            value: (recommendationSummary.num_custom / Math.max(recommendationSummary.total_recs, 1)) * 100,
            color: `${theme.palette.background.customProgress}`
        },
        {
            value: (recommendationSummary.num_rejected / Math.max(recommendationSummary.total_recs, 1)) * 100,
            color: `${theme.palette.background.deniedProgress}`
        },
        {
            value: (recommendationSummary.num_open / Math.max(recommendationSummary.total_recs, 1)) * 100,
            color: `${theme.palette.background.unreviewedProgress}`
        }
    ];

    return (
        <TableRow key={props.physician.id} sx={{ height: 'auto' }}>
            <PhysicianContentCell width='14%' textAlign='left'>
                <Typography variant='normal' fontSize='12px' title={`Dr. ${props.physician.last_name}, ${props.physician.first_name}`}>
                    Dr. {props.physician.last_name}, {props.physician.first_name}
                </Typography>
            </PhysicianContentCell>
            <PhysicianContentCell width='35%' textAlign='left'>
                <ProgressTooltip 
                    acceptedCount={recommendationSummary.num_accepted} 
                    acceptedValue={recommendationSummary.accepted_value / 100} 
                    customCount={recommendationSummary.num_custom} 
                    customValue={recommendationSummary.custom_value / 100}
                    deniedCount={recommendationSummary.num_rejected} 
                    deniedValue={recommendationSummary.rejected_value / 100} 
                    unreviewedCount={recommendationSummary.num_open} 
                    unreviewedValue={recommendationSummary.open_value / 100}
                >
                    <Box>
                        <MultipleProgressBar segments={props.numericFormat === 'currency' ? progressValues : progressCounts} label={
                            props.numericFormat === 'currency' ? 
                                `$${reviewedValueK}k / $${totalValueK}k (${reviewedValuePercent}%)` : 
                                `${reviewedCount} / ${recommendationSummary.total_recs} Optimizations (${reviewedCountPercent}%)`
                        } />
                    </Box>
                </ProgressTooltip>
            </PhysicianContentCell>
            <PhysicianContentCell width='12%' textAlign='left'>
                <Typography variant='normal' fontSize='12px'>
                    { props.numericFormat === 'currency' && 
                        <NumericFormat value={opportunityValue} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={0} fixedDecimalScale />
                    }
                    { props.numericFormat === 'count' && 
                        `${recommendationSummary.original_recs} Optimizations`
                    }
                </Typography>
            </PhysicianContentCell>
            <PhysicianContentCell width='12%' textAlign='left'>
                <Typography variant='normal' fontSize='12px'>
                    { props.numericFormat === 'currency' &&
                        <NumericFormat value={approvedValue} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${Math.round(approvedPercent)}%)`} decimalScale={0} fixedDecimalScale />
                    }
                    { props.numericFormat === 'count' &&
                        `${approvedCount} Optimizations (${approvedCountPercent}%)`
                    }
                </Typography>
            </PhysicianContentCell>
            <PhysicianContentCell width='12%' textAlign='left'>
                <Typography variant='normal' fontSize='12px'>
                    { props.numericFormat === 'currency' &&
                        <NumericFormat value={deniedValue} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${Math.round(deniedPercent)}%)`} decimalScale={0} fixedDecimalScale />
                    }
                    { props.numericFormat === 'count' &&
                        `${recommendationSummary.num_rejected} Optimizations (${deniedCountPercent}%)`
                    }
                </Typography>
            </PhysicianContentCell>
            <PhysicianContentCell width='12%' textAlign='left'>
                <Typography variant='normal' fontSize='12px'>
                    { props.numericFormat === 'currency' &&
                        <NumericFormat value={unreviewedValue} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${Math.round(unreviewedPercent)}%)`} decimalScale={0} fixedDecimalScale />
                    }
                    { props.numericFormat === 'count' &&
                        `${recommendationSummary.num_open} Optimizations (${unreviewedCountPercent}%)`
                    }
                </Typography>
            </PhysicianContentCell>
            <PhysicianContentCell width='12%' textAlign='left'>
                <Typography variant='normal' fontSize='12px'>
                    { props.numericFormat === 'currency' &&
                        <NumericFormat value={omittedValue} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={` (${Math.round(omittedPercent)}%)`} decimalScale={0} fixedDecimalScale />
                    }
                    { props.numericFormat === 'count' &&
                        `${recommendationSummary.num_omitted} Optimizations (${omittedCountPercent}%)`
                    }
                </Typography>
            </PhysicianContentCell>
        </TableRow>
    );
}

function PhysicianContentCell(props) {
    return (
        <TableCell sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: props.width, maxWidth: 0, textAlign: props.textAlign }}>
            {props.children}
        </TableCell>
    );
}

export default PhysicianRow;
